import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { TRACK } from '@disco/constants/ItemTypes'
import {
  alterPlaylistGroups,
  alterTrackGroups,
  updateUserSettingsUnsafe,
} from '@disco/state-management/actions/ExplorerActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  getExplorerSelectedItems,
  getExplorerSelectedPlaylists,
  getExplorerSelectedTracks,
  getExplorerSelectionGroup,
} from '@disco/state-management/selectors/selection'
import { getHideChannelRemovalSetting } from '@disco/state-management/selectors/user'

import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import type { UserSettings } from 'types/user'

import DocumentHeadTags from 'components/document-head-tags'
import ChannelRemovalDialog from 'components/modals/dialogs/channel-removal-dialog'
import { getSelectionReturnPath } from 'utils/routing'

function mapStateToProps(state) {
  const selectedItems = getExplorerSelectedItems(state)
  const selectedTracks = getExplorerSelectedTracks(state)
  const selectedPlaylists = getExplorerSelectedPlaylists(state)
  return {
    hideRemovalWarning: getHideChannelRemovalSetting(state),
    selectionGroup: getExplorerSelectionGroup(state),
    settings: state.user.settings,
    selectedTracks: selectedTracks.filter(
      (item: Track) => item.type === TRACK && !item.outside_your_territories,
    ),
    selectedPlaylists,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      alterTrackGroups,
      alterPlaylistGroups,
      updateUserSettingsUnsafe,
      resetSelection,
    },
    dispatch,
  )
}

type Props = {
  alterTrackGroups: typeof alterTrackGroups
  alterPlaylistGroups: typeof alterPlaylistGroups
  resetSelection: typeof resetSelection
  updateUserSettingsUnsafe: typeof updateUserSettingsUnsafe
  settings: UserSettings
  selectionGroup: Group
  selectedTracks: Track[]
  selectedPlaylists: Playlist[]
  hideRemovalWarning: boolean
}

function RemoveFromChannelComponent(props: Props) {
  const navigate = useNavigate()
  const location = useLocation()

  const onClose = React.useCallback(() => {
    navigate(getSelectionReturnPath(location.pathname))
  }, [navigate, location.pathname])

  const handleDelete = React.useCallback(() => {
    const { selectedTracks, selectedPlaylists, selectionGroup } = props

    if (selectedTracks.length) {
      props.alterTrackGroups(selectedTracks, [], [selectionGroup])
    }

    if (selectedPlaylists.length) {
      props.alterPlaylistGroups(selectedPlaylists, [], [selectionGroup])
    }

    props.resetSelection()

    onClose()
  }, [
    props.selectedTracks,
    props.selectedPlaylists,
    props.selectionGroup,
    props.alterTrackGroups,
    props.alterPlaylistGroups,
    props.resetSelection,
    onClose,
  ])

  const hideWarning = React.useCallback(() => {
    props.updateUserSettingsUnsafe({
      settings: {
        hide_channel_removal_warning: !props.hideRemovalWarning,
      },
    })
  }, [props.updateUserSettingsUnsafe, props.hideRemovalWarning])

  return (
    <div>
      <DocumentHeadTags title="Remove from channel | DISCO" />
      <ChannelRemovalDialog
        onConfirm={handleDelete}
        onCancel={onClose}
        onHideWarningChange={hideWarning}
        hideWarning={props.hideRemovalWarning}
      />
    </div>
  )
}

const RemoveFromChannel = connect(
  mapStateToProps,
  mapDispatchToProps,
)(RemoveFromChannelComponent)
export default RemoveFromChannel
